import React, { useContext, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { CheckinPaymentDetail } from '@/components/organisms/self-checkin/payment/checkin-payment-detail'
import { selfCheckinHandlePaymentByCard } from '@/apis/aipass'
import { CASH, CASH_CHANGER, CREDIT_CARD, PaymentMethod } from '@/models/payment-method'
import { useForm } from 'react-hook-form'
import { CheckinPaymentMethod } from '@/components/organisms/self-checkin/payment/payment-method-select'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { useCardToken } from '@/hooks/use-card-token'
import { useGuestAppUrl } from '@/hooks/use-guest-app-url'
import { LoaderContextCreator } from '@/contexts/loader'
import { PaymentResultByCash, PaymentResultByCredit } from '@/models/self-checkin'
import { reportError } from '@/components/apps/error-boundary'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'

export type CardInfo = {
  cardNumber: string
  expiredMonth: string
  expiredYear: string
  holderName: string
  cvc: string
}

export const ManualPaymentScreen: React.FC = () => {
  const { state, doCheckIn } = useSelfCheckInState()
  const history = useHistory()
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>()
  const { isLoading, setIsLoading } = useContext(LoaderContextCreator())

  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { makeCardToken } = useCardToken()
  const cardForm = useForm<CardInfo>({
    mode: 'onBlur',
    defaultValues: {
      cardNumber: '',
      expiredMonth: '',
      expiredYear: '',
      holderName: '',
      cvc: '',
    },
  })
  const { mypageUrl, homePageUrl } = useGuestAppUrl({ hotelId: state.hotelId, plugin: state.setting.plugin })

  const onSubmit = async (paymentResultByCash?: PaymentResultByCash) => {
    if (!validateCardInfo) {
      return
    }
    let paymentResult: PaymentResultByCash | PaymentResultByCredit | undefined = paymentResultByCash
    try {
      setIsLoading(true)
      if (selectedPaymentMethod === CREDIT_CARD) {
        try {
          const cardInfo = cardForm.getValues()
          const cardToken = await makeCardToken(state.hotelId, {
            cardNumber: cardInfo.cardNumber.replaceAll(' ', ''),
            cardExpire: `${cardInfo.expiredMonth}/${cardInfo.expiredYear}`,
            securityCode: cardInfo.cvc,
            cardHolder: cardInfo.holderName,
          })
          paymentResult = await selfCheckinHandlePaymentByCard({
            hotelId: state.hotelId,
            reservationIdWithAssignIds: state.selectReservation!.selectedReservationAndRooms,
            cardToken,
          })
        } catch (error) {
          setIsLoading(false)
          console.error(error)
          alert(t('Your payment was declined by your card issuer Please connect your card issuer'))
          return
        }
      }
      await doCheckIn({
        state,
        language,
        guestAppUrl: { mypageUrl, homePageUrl },
        selectedPaymentMethod: selectedPaymentMethod || CASH,
        paymentResult,
      })
      history.replace({ pathname: '/self-checkin/issue-room-key' })
    } catch (e) {
      reportError(e)
      alert(t('Please complete the procedure at the front desk'))
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
    } finally {
      setIsLoading(false)
    }
  }

  const enabledPaymentSetting = useMemo(() => {
    const enabledSetting = state.setting.payment.map(setting =>
      setting === CASH && state.setting.isEnabledCashChanger ? CASH_CHANGER : setting,
    )
    if (!enabledSetting.length) {
      return []
    }
    setSelectedPaymentMethod(enabledSetting[0])
    return enabledSetting
  }, [])

  const validateCardInfo = useMemo(() => {
    if (selectedPaymentMethod === CASH) {
      return true
    }
    return cardForm.formState.isValid
  }, [selectedPaymentMethod, cardForm.formState.isValid])

  if (!state.confirm?.payment) {
    return <></>
  }
  return (
    <SelfCheckinLayout
      state={state}
      header={{ goToPreviousPage: history.goBack, title: t('Payment details') }}
      footer={{
        right: {
          isDisabled: isLoading || !validateCardInfo,
          label: t('Submit'),
          onClick: () => onSubmit(),
        },
      }}
    >
      <div>
        {state.confirm.payment.usage.reservations.map(
          (reservation, index) =>
            !!reservation.sales.length && (
              <div css={wrapperStyle} key={index}>
                <CheckinPaymentDetail reservation={reservation} index={index} />
              </div>
            ),
        )}
      </div>
      <div>
        <div css={commonContainerStyle}>
          <div css={amountStyle}>
            <p css={amountItem}>{t('Amount billed')}</p>
            <p css={amountValue}>¥{state.confirm.payment.usage.totalAmount}</p>
          </div>
          <p css={taxStyle}>
            ({t('Tax')}：¥{state.confirm.payment.usage.totalTaxPrice})
          </p>
        </div>
      </div>

      <div>
        <div css={paymentStyle}>
          <CheckinPaymentMethod
            onChangePaymentMethod={e => {
              cardForm.reset({
                cardNumber: '',
                expiredMonth: '',
                expiredYear: '',
                holderName: '',
                cvc: '',
              })
              setSelectedPaymentMethod(e.target.value)
            }}
            selectedPaymentMethod={selectedPaymentMethod}
            paymentSetting={enabledPaymentSetting}
            cardForm={cardForm}
          />
        </div>
      </div>
    </SelfCheckinLayout>
  )
}

const wrapperStyle = css(
  {
    background: 'transparent',
    margin: '0 auto',
    borderRadius: '10px',
  },
  createSelfCheckinResponsiveStyle({
    portrait: { borderRadius: 0 },
  }),
)

const paymentStyle = css({
  margin: '0 auto',
})

const commonContainerStyle = css(
  {
    backgroundColor: '#fff',
    textAlign: 'left',
    padding: '30px 32px 30px 32px',
    margin: '0 auto',
    borderRadius: '10px',
  },
  createSelfCheckinResponsiveStyle({
    portrait: { borderRadius: 0 },
  }),
)

const amountStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})

const amountItem = css({
  fontSize: '20px',
  color: '#d68e0f',
  fontWeight: 'bold',
  letterSpacing: '2.7px',
})
const amountValue = css(amountItem, {
  fontSize: '22px',
})

const taxStyle = css({
  textAlign: 'right',
  width: '100%',
  color: '#676767',
  fontSize: '14px',
  letterSpacing: '2.1px',
  marginTop: '2px',
  lineHeight: '19px',
})
